"use strict";

/**
 * The full inspection record, read only (the Plan's "Open record"). Pure: it
 * turns what the existing endpoints return into the sheet's model.
 *
 *   amendmentLines(amendments)        -> one line per change: who, what, when
 *   buildRecordSheetModel(parts)      -> { number, title, facts, parties, ... }
 *
 * `row` is the register row from CisInspectionsQuery in the "latest" view, so
 * every amendment is already applied to it. `amendments` come from
 * CisAmendmentsList, `rows` is the project's register (for the repair link),
 * `ncr` the NCR found for this record by the panel's own lookup.
 */

import { formatDay, formatDayTime, initialsName } from "./shape.js";
import { recordReviewView } from "./recordReview.js";

const FIELD_LABEL = {
  comments: "comments",
  result: "result",
  defectNoted: "defect noted",
  defectText: "defect text",
  ownerRep: "owner rep",
  classRep: "class rep",
  makerRep: "maker rep",
  yardQc: "yard QC",
  subcontractor: "subcontractor",
};

const text = (v) => (v === undefined || v === null ? "" : String(v).trim());

function valueText(key, value) {
  if (key === "defectNoted") return value === true ? "yes" : "no";
  const t = text(value);
  return t || "empty";
}

/** "who changed what, when" for each amendment, oldest first. */
export function amendmentLines(amendments) {
  const list = Array.isArray(amendments) ? amendments : [];
  return list
    .slice()
    .sort((a, b) => Number(a.seq || 0) - Number(b.seq || 0))
    .map((a, i) => {
      const keys = Object.keys(a.fields || {});
      const changes = keys.map((k) => `${FIELD_LABEL[k] || k} to ${valueText(k, a.fields[k])}`);
      const what = a.void === true ? "Voided the record" : changes.length ? `Changed ${changes.join(", ")}` : "Recorded a change";
      return {
        key: `${a.seq === undefined ? i : a.seq}`,
        who: initialsName(a.amendedBy),
        when: a.amendedAtUtc ? formatDayTime(a.amendedAtUtc) : "",
        what,
        reason: text(a.reason),
        notApplied: a.superseded_invalid === true,
      };
    });
}

function party(label, value) {
  const t = text(value);
  return { label, value: t || "Not recorded", present: Boolean(t) };
}

/**
 * @param {object} parts
 * @param {object} parts.row      the register row, latest view
 * @param {object[]} [parts.rows] the project's register rows
 * @param {object[]} [parts.amendments]
 * @param {object|null} [parts.ncr] { code, ncrNumber, title }
 */
export function buildRecordSheetModel({ row, rows = [], amendments = [], ncr = null }) {
  const defect = row.defectNoted === true;
  const facts = [
    ["Date", row.inspectionDate ? formatDay(`${String(row.inspectionDate).slice(0, 10)}T00:00:00Z`) : "Not recorded"],
    ["Category", text(row.category) || "Not recorded"],
    ["Stage", text(row.stage) || "Not recorded"],
    ["Block", text(row.block) || "Not recorded"],
    ["Side", text(row.side) || "Not recorded"],
    ["Result", text(row.result) || "Not recorded"],
    ["Defect noted", defect ? "Yes" : "No"],
  ];
  if (defect) facts.push(["Defect", text(row.defectText) || "No defect text recorded"]);
  const captured = [
    row.capturedBy ? `Captured by ${initialsName(row.capturedBy)}` : "Captured",
    row.capturedAtUtc ? formatDayTime(row.capturedAtUtc) : "",
  ].filter(Boolean);
  return {
    inspectionNumber: row.inspectionNumber,
    number: `CI-${row.inspectionNumber}`,
    title: text(row.inspectionItem) || `Inspection ${row.inspectionNumber}`,
    facts,
    comments: text(row.comments),
    parties: [
      party("Owner rep", row.ownerRep),
      party("Class rep", row.classRep),
      party("Maker rep", row.makerRep),
      party("Yard QC", row.yardQc),
      party("Subcontractor", row.subcontractor),
    ],
    captured: captured.join(", "),
    voided: row.voided === true,
    review: recordReviewView(row, rows),
    amendments: amendmentLines(amendments),
    ncr: ncr ? { code: ncr.code, ncrNumber: ncr.ncrNumber, title: ncr.title || "" } : null,
  };
}
